import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useUser } from "./UserContext";
import { buildApiUrl } from "../config/api.config";
import { parseJsonResponse } from "../utils/apiResponse";

const SubscriptionContext = createContext(null);

const TRIAL_FEATURES = [
  "dashboard", "customers", "suppliers", "inventory", "materials",
  "production", "sales", "payments", "purchases", "expenses", "reports",
  "advanced_reports", "barcodes", "team", "settings",
];

function hasActiveTrial(status) {
  if (!status?.trial_end_date) return false;
  const trialEnd = new Date(status.trial_end_date);
  if (Number.isNaN(trialEnd.getTime()) || new Date() > trialEnd) return false;
  const subscriptionState = String(status.subscription_status || "").toLowerCase();
  return subscriptionState === "trial" || (!status.subscription_start_date && String(status.subscription_plan || "free").toLowerCase() === "free");
}

function subscriptionFromUser(user) {
  if (!user?.id || !user?.subscription_status) return null;
  const trialTimestamp = user.trial_end_date
    ? Math.floor(new Date(user.trial_end_date).getTime() / 1000)
    : 0;
  return {
    subscription_plan: user.subscription_plan || "free",
    subscription_status: user.subscription_status,
    subscription_start_date: user.subscription_start_date || null,
    subscription_end_date: user.subscription_end_date || null,
    trial_start_date: user.trial_start_date || null,
    trial_end_date: user.trial_end_date || null,
    expiration_timestamp: Number.isFinite(trialTimestamp) ? trialTimestamp : 0,
    is_expired: user.trial_end_date
      ? new Date(user.trial_end_date) < new Date()
      : false,
    inherited_from: "self",
    is_company_subscription: false,
  };
}

function cachedSubscriptionForUser(user) {
  if (!user?.id) return null;
  try {
    const cachedUserId = localStorage.getItem("subscription_user_id");
    const cachedCompanyId = localStorage.getItem("subscription_company_id");
    if (
      String(cachedUserId || "") !== String(user.id) ||
      String(cachedCompanyId || "") !== String(user.company_id || "")
    ) return subscriptionFromUser(user);
    const cached = localStorage.getItem("subscription_status");
    return cached ? JSON.parse(cached) : subscriptionFromUser(user);
  } catch {
    return subscriptionFromUser(user);
  }
}

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated, logout } = useUser();

  // Initialize from localStorage to prevent flashing wrong state
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => cachedSubscriptionForUser(user));

  const [isLoading, setIsLoading] = useState(true);
  const expirationRefreshKeyRef = useRef(null);
  const activeRequestRef = useRef(null);
  const rejectedSessionRef = useRef(false);
  const [lastCheckTime, setLastCheckTime] = useState(() => {
    try {
      const cached = localStorage.getItem("subscription_last_check");
      return cached ? parseInt(cached) : null;
    } catch {
      return null;
    }
  });

  // Check if we should fetch subscription (only 3 times per day)
  const shouldFetchSubscription = useCallback(() => {
    if (!lastCheckTime) return true;

    const now = Date.now();
    const hoursSinceLastCheck = (now - lastCheckTime) / (1000 * 60 * 60);

    // Check every 8 hours (3 times per day)
    return hoursSinceLastCheck >= 8;
  }, [lastCheckTime]);

  // Fetch subscription status from API
  const fetchSubscriptionStatus = useCallback(
    (force = false) => {
      if (!isAuthenticated || !user?.id || rejectedSessionRef.current) {
        setSubscriptionStatus(null);
        setIsLoading(false);
        return Promise.resolve(null);
      }

      // Skip if not forced and already checked recently
      if (!force && !shouldFetchSubscription()) {
        setIsLoading(false);
        return Promise.resolve(null);
      }

      // StrictMode, the expiry boundary and manual refresh can overlap. Share
      // one request so the application never floods the endpoint or console.
      if (activeRequestRef.current) return activeRequestRef.current;

      setIsLoading(true);
      let request;
      request = (async () => {
        try {
          const response = await fetch(
            buildApiUrl("api/subscription.php?action=current"),
            {
              method: "GET",
              credentials: "include",
              headers: { Accept: "application/json" },
            }
          );

          if (response.status === 401) {
            // A 401 is an explicit server verdict, not a transient network
            // failure. Clear stale entitlements and perform one clean logout.
            if (!rejectedSessionRef.current) {
              rejectedSessionRef.current = true;
              setSubscriptionStatus(null);
              setLastCheckTime(null);
              try {
                localStorage.removeItem("subscription_status");
                localStorage.removeItem("subscription_last_check");
                localStorage.removeItem("subscription_user_id");
                localStorage.removeItem("subscription_company_id");
              } catch {
                // Storage can be unavailable in privacy-restricted browsers.
              }
              await logout();
            }
            return null;
          }

          if (!response.ok) {
            console.warn("Subscription status is temporarily unavailable:", response.status);
            return null;
          }

          const data = await parseJsonResponse(response);

          if (data.success && data.data) {
            rejectedSessionRef.current = false;
            setSubscriptionStatus(data.data);
            const checkedAt = Date.now();
            setLastCheckTime(checkedAt);

            // Persist to localStorage to prevent flashing on refresh
            try {
              localStorage.setItem(
                "subscription_status",
                JSON.stringify(data.data)
              );
              localStorage.setItem(
                "subscription_last_check",
                checkedAt.toString()
              );
              localStorage.setItem("subscription_user_id", String(user.id));
              localStorage.setItem("subscription_company_id", String(user.company_id || ""));
            } catch (err) {
              console.warn("Failed to cache subscription status:", err);
            }
          } else {
            console.warn(
              "Subscription API returned no usable status; keeping the current identity-scoped entitlement:",
              data
            );
          }
          return data;
        } catch (err) {
          console.error("Error fetching subscription status:", err);
          // Preserve the last known entitlement on genuine network failures.
          return null;
        } finally {
          if (activeRequestRef.current === request) activeRequestRef.current = null;
          setIsLoading(false);
        }
      })();

      activeRequestRef.current = request;
      return request;
    },
    [isAuthenticated, logout, shouldFetchSubscription, user?.company_id, user?.id]
  );

  // Initialize subscription check on mount/login only
  useEffect(() => {
    rejectedSessionRef.current = false;
    expirationRefreshKeyRef.current = null;
    if (!isAuthenticated || !user?.id) {
      setSubscriptionStatus(null);
      setIsLoading(false);
      return;
    }

    // Reset identity-scoped status immediately. OTP registration carries its
    // signed trial dates in the returned user object, so the new workspace is
    // usable before this background refresh completes.
    setSubscriptionStatus(cachedSubscriptionForUser(user));
    fetchSubscriptionStatus(true); // Force check on mount/login
    // This check intentionally follows identity changes only; adding the
    // callback would also refetch whenever its cache timestamp changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, user?.company_id]); // Refetch when the identity or active store changes.

  // Periodic check every 8 hours
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const interval = setInterval(() => {
      if (shouldFetchSubscription()) {
        console.log("🔄 Periodic subscription check (8 hours)");
        fetchSubscriptionStatus(true);
      }
    }, 1000 * 60 * 60); // Check every hour if 8 hours have passed

    return () => clearInterval(interval);
  }, [isAuthenticated, user, shouldFetchSubscription, fetchSubscriptionStatus]);

  // Refresh at the expiry boundary without publishing a new context value
  // every second. The visible countdown lives in SubscriptionBanner so the
  // modern and classic sidebars do not continually re-render.
  useEffect(() => {
    const expirationTimestamp = Number(subscriptionStatus?.expiration_timestamp || 0);
    if (!expirationTimestamp) return undefined;

    let timeoutId = 0;
    const maxTimeout = 2_147_000_000;
    const refreshAtExpiration = () => {
      const remainingMs = (expirationTimestamp * 1000) - Date.now();
      if (remainingMs > 0) {
        timeoutId = window.setTimeout(refreshAtExpiration, Math.min(remainingMs + 250, maxTimeout));
        return;
      }

      const refreshKey = [
        user?.id || "anonymous",
        user?.company_id || "no-company",
        expirationTimestamp,
      ].join(":");
      if (expirationRefreshKeyRef.current !== refreshKey) {
        expirationRefreshKeyRef.current = refreshKey;
        fetchSubscriptionStatus(true);
      }
    };

    refreshAtExpiration();
    return () => window.clearTimeout(timeoutId);
  }, [subscriptionStatus?.expiration_timestamp, fetchSubscriptionStatus, user?.company_id, user?.id]);

  // Check if user has valid subscription (trial or paid)
  const hasValidSubscription = useCallback(() => {
    if (!subscriptionStatus) {
      return false;
    }

    // Check for active paid subscription
    if (
      subscriptionStatus.subscription_status === "active" &&
      subscriptionStatus.subscription_plan !== "free" &&
      subscriptionStatus.subscription_start_date
    ) {
      // Verify not expired
      if (subscriptionStatus.subscription_end_date) {
        const now = new Date();
        const endDate = new Date(subscriptionStatus.subscription_end_date);
        return now <= endDate;
      }
      return true;
    }

    if (hasActiveTrial(subscriptionStatus)) return true;

    return false;
  }, [subscriptionStatus]);

  // Check if user is on trial
  const isTrialActive = useCallback(() => {
    if (!subscriptionStatus) return false;

    return hasActiveTrial(subscriptionStatus);
  }, [subscriptionStatus]);

  // Get days remaining (trial or subscription)
  const getDaysRemaining = useCallback(() => {
    if (!subscriptionStatus) return 0;

    const endDate =
      subscriptionStatus.subscription_end_date ||
      subscriptionStatus.trial_end_date;

    if (!endDate) return 0;

    const now = new Date();
    const end = new Date(endDate);

    if (now > end) return 0;

    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }, [subscriptionStatus]);

  // Get trial progress percentage (0-100)
  const getTrialProgress = useCallback(() => {
    if (
      !subscriptionStatus?.trial_start_date ||
      !subscriptionStatus?.trial_end_date
    ) {
      return 100;
    }

    const start = new Date(subscriptionStatus.trial_start_date);
    const end = new Date(subscriptionStatus.trial_end_date);
    const now = new Date();

    const total = end - start;
    const elapsed = now - start;

    if (elapsed <= 0) return 0;
    if (elapsed >= total) return 100;

    return Math.round((elapsed / total) * 100);
  }, [subscriptionStatus]);

  // Get allowed features based on plan
  const getAllowedFeatures = useCallback(() => {
    if (!subscriptionStatus) return [];

    // The free trial is a real evaluation period, not the permanently limited
    // free plan. Let new companies explore every business workspace module.
    if (hasActiveTrial(subscriptionStatus)) return TRIAL_FEATURES;

    const plan = subscriptionStatus.subscription_plan || "free";
    const planFeatures = {
      free: ["dashboard", "customers", "suppliers", "inventory", "settings"],
      starter: [
        "dashboard",
        "customers",
        "suppliers",
        "inventory",
        "sales",
        "payments",
        "settings",
      ],
      professional: [
        "dashboard",
        "customers",
        "suppliers",
        "inventory",
        "sales",
        "payments",
        "purchases",
        "expenses",
        "reports",
        "materials",
        "production",
        "barcodes",
        "settings",
      ],
      enterprise: [
        "dashboard",
        "customers",
        "suppliers",
        "inventory",
        "sales",
        "payments",
        "purchases",
        "expenses",
        "reports",
        "advanced_reports",
        "materials",
        "production",
        "barcodes",
        "settings",
      ],
    };

    return planFeatures[plan] || planFeatures.free;
  }, [subscriptionStatus]);

  // Check if user can access a specific feature
  const canAccessFeature = useCallback(
    (feature) => {
      const allowed = getAllowedFeatures();
      return allowed.includes(feature.toLowerCase());
    },
    [getAllowedFeatures]
  );

  // Get status message
  const getStatusMessage = useCallback(() => {
    if (!subscriptionStatus) return "No subscription";

    const isTrial = !subscriptionStatus.subscription_start_date;
    const daysLeft = getDaysRemaining();

    if (isTrial) {
      if (daysLeft > 0) {
        return `Trial: ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`;
      }
      return "Trial expired";
    }

    if (subscriptionStatus.subscription_status === "active") {
      if (daysLeft > 0) {
        return `Active: ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`;
      }
      return "Active";
    }

    return subscriptionStatus.subscription_status;
  }, [subscriptionStatus, getDaysRemaining]);

  // Force refresh subscription (for manual refresh)
  const refreshSubscription = useCallback(() => {
    return fetchSubscriptionStatus(true);
  }, [fetchSubscriptionStatus]);

  const value = {
    subscriptionStatus,
    isLoading,
    hasValidSubscription: hasValidSubscription(),
    isTrialActive: isTrialActive(),
    daysRemaining: getDaysRemaining(),
    getDaysRemaining,
    trialProgress: getTrialProgress(),
    allowedFeatures: getAllowedFeatures(),
    canAccessFeature,
    statusMessage: getStatusMessage(),
    refreshSubscription,
    // Helper properties
    plan: subscriptionStatus?.subscription_plan || "free",
    status: subscriptionStatus?.subscription_status || "inactive",
    isTrial: !subscriptionStatus?.subscription_start_date,
    requiresSubscription: !hasValidSubscription(),
    // Subscription inheritance
    isInherited: subscriptionStatus?.is_company_subscription || false,
    inheritedFrom: subscriptionStatus?.inherited_from || "self",
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// This context keeps its provider and consumer hook together as one public API.
// eslint-disable-next-line react-refresh/only-export-components
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};
